import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2 } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { useSiteContent } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const EMPTY = { name: "", business: "", email: "", phone: "", product: "", message: "" };

const inputCls =
  "w-full bg-[#F7FAFD] border border-[#263562]/10 rounded-lg px-4 py-3.5 text-sm text-[#1E2A4A] placeholder:text-[#949CA1] outline-none focus:border-[#3AB0E1] focus:ring-1 focus:ring-[#3AB0E1]/30 transition-all duration-300";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const content = useSiteContent();

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, product: e.detail }));
    window.addEventListener("prefill-product", onPrefill);
    return () => window.removeEventListener("prefill-product", onPrefill);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received", {
        description: "Our Nairobi team will get back to you within one business day.",
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry", {
        description: "Please try again, or email us directly at info@hostechsolutions.co.ke.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-36 overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="absolute -bottom-40 right-0 w-[700px] h-[500px] bg-[radial-gradient(circle,rgba(58,176,225,0.10),transparent_65%)]"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#263562]/60">
            Start the Conversation
          </span>
          <h2 className="mt-4 font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl text-[#263562]">
            Let's spec your{" "}
            <span className="text-[#3AB0E1]">property's stack</span>.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#5A6B85] font-light">
            Migrating from a legacy PMS, opening a new outlet, or going KRA
            eTIMS-compliant? Tell us about your operation and we'll recommend the
            right HostPoint, HTI or PassportScan combination — usually within one
            business day.
          </p>
          <div className="mt-12 space-y-6">
            {[
              { icon: MapPin, label: "Headquarters", value: content.contactHq },
              { icon: Mail, label: "Email", value: content.contactEmail, href: `mailto:${content.contactEmail}` },
              { icon: Phone, label: "Phone / WhatsApp", value: content.contactPhone, href: `tel:${content.contactPhone.replace(/[^+\d]/g, "")}` },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-lg border border-[#263562]/10 bg-white shadow-sm flex items-center justify-center group-hover:border-[#3AB0E1]/60 transition-colors duration-300">
                  <c.icon className="w-[18px] h-[18px] text-[#263562]" />
                </span>
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1]">
                    {c.label}
                  </p>
                  {c.href ? (
                    <a href={c.href} className="text-[#263562] hover:text-[#3AB0E1] transition-colors">
                      {c.value}
                    </a>
                  ) : (
                    <p className="text-[#263562]">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-2xl border border-[#263562]/10 bg-white p-8 sm:p-10 shadow-[0_30px_60px_-20px_rgba(38,53,98,0.18)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
                Name *
              </label>
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Amina Otieno"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
                Business / Property
              </label>
              <input
                data-testid="contact-business-input"
                value={form.business}
                onChange={set("business")}
                placeholder="The Diani Plate"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
                Email *
              </label>
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.co.ke"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
                Phone
              </label>
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+254 7XX XXX XXX"
                className={inputCls}
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
              System of Interest
            </label>
            <select
              data-testid="contact-product-select"
              value={form.product}
              onChange={set("product")}
              className={`${inputCls} appearance-none ${form.product ? "" : "text-[#949CA1]"}`}
            >
              <option value="" className="bg-white">Select a system…</option>
              {PRODUCTS.map((p) => (
                <option key={p.id} value={p.name} className="bg-white">
                  {p.name}
                </option>
              ))}
              <option value="Full Stack Consultation" className="bg-white">
                Not sure — recommend a full stack
              </option>
            </select>
          </div>
          <div className="mt-5">
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
              How can we help? *
            </label>
            <textarea
              data-testid="contact-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="We run a 40-room lodge with two restaurants and need PMS, POS and eTIMS compliance…"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 w-full group flex items-center justify-center gap-3 bg-[#263562] hover:bg-[#3AB0E1] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_10px_40px_rgba(58,176,225,0.4)]"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send Enquiry
                <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </>
            )}
          </button>
          <p className="mt-4 text-center text-xs text-[#949CA1]">
            We'll only use your details to respond to your enquiry.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
